import { useState, useMemo, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import FaqAccordion from '../components/FaqAccordion';
import Lightbox from '../components/Lightbox';
import { imageUrl } from '../lib/images';
import { getSeoPageByPath, SEO_PAGES_DATA } from '../data/seoPagesData';
import { createProseLinker } from '../lib/proseLinks';
import { generateEventId, trackMetaLead } from '../lib/metaTracking';

// Image galleries mapped to services for authentic visual proof
const SERVICE_GALLERY_MAP = {
  '/autohjaelp': [
    { src: '/_assets/bjaergningsvogn-autohjaelp-havareret-lastbil.jpg', title: 'Akut vejhjælp og assistance' },
    { src: '/_assets/volvo-fejeblad-det-roede-lyn.jpg', title: 'Det Røde Lyn · Hurtig udrykning' },
    { src: '/_assets/service-vogn-1-198.jpg', title: 'Servicevogn til mindre reparationer' },
  ],
  '/bugsering-af-bil': [
    { src: '/_assets/man-autotransporter-fejeblad-bugsering.png', title: 'Bugsering med lad og brille' },
    { src: '/_assets/mc-autotransporter-med-skadede-biler.png', title: 'Transport af skadede biler' },
    { src: '/_assets/vogn-60-med-biler-paa-lad-og-i-grill-18-03-2025-324.jpg', title: 'Vogn 60 med biler på lad' },
  ],
  '/bugsering-af-lastbil': [
    { src: '/_assets/bjaerger-med-lastbil-18-03-2025-347.jpg', title: 'Bugsering af tungvogn og lastbil' },
    { src: '/_assets/volvo-bjaergningslastbil-motorvej.jpg', title: 'Assistance til havareret tankvogn' },
    { src: '/_assets/scania-lastbil-blokvogn-transport.jpg', title: 'Transport på blokvogn' },
  ],
  '/bjaergning': [
    { src: '/_assets/volvo-bjaergningsvogn-motorvej-assistance.png', title: 'Scania R660 bjærgningsvogn på motorvej' },
    { src: '/_assets/gul-bjaergningsvogn-mc-transport.jpg', title: 'Gul tung bjærger' },
    { src: '/_assets/bjaergning-med-uni-man-og-scania-18-03-2025-351.jpg', title: 'Bjærgning med Uni Man og Scania' },
  ],
  '/tow-truck': [
    { src: '/_assets/volvo-bjaergningslastbil-motorvej.jpg', title: 'Tow truck assistance Denmark' },
    { src: '/_assets/volvo-fejeblad-det-roede-lyn.jpg', title: 'Flatbed recovery truck' },
    { src: '/_assets/man-autotransporter-fejeblad-bugsering.png', title: 'Roadside vehicle recovery' },
  ],
  '/fritraekning': [
    { src: '/_assets/uni-man-4x4-bjaergningskoeretoej.jpg', title: 'Uni Man 4x4 terrængående fritrækning' },
    { src: '/_assets/scania-bjaerger-18-03-2025-forside-233.jpg', title: 'Scania 4-akslet tung fritrækning' },
    { src: '/_assets/bjaergning-med-uni-man-og-scania-18-03-2025-232.jpg', title: 'Assistance i vanskeligt terræn' },
  ],
  '/skiltevogn': [
    { src: '/_assets/roed-volvo-og-2-lastbiler-18-03-2025-411.jpg', title: 'Materiel til afmærkning og transport' },
    { src: '/_assets/roed-volvo-med-militaer-udstyr-18-03-2025-224.jpg', title: 'Sikkerhed ved vejen' },
    { src: '/_assets/annexgaardsvej-7-luftfoto-1-305.jpg', title: 'MC Transport base i Ugerløse' },
  ],
  '/TMA': [
    { src: '/_assets/roed-volvo-med-militaer-udstyr-18-03-2025-224.jpg', title: 'Påkørselssikring og TMA-materiel' },
    { src: '/_assets/traekker-og-trailer-nyvasket-3-245.jpg', title: 'Materiel og kørsel' },
    { src: '/_assets/annexgaardsvej-7-luftfoto-2-306.jpg', title: 'Pladsen i Ugerløse' },
  ],
  '/daekcenter-lastbil': [
    { src: '/_assets/service-vogn-1-198.jpg', title: 'Servicevogn til dækservice på lastbil' },
    { src: '/_assets/servicevogn-2-202.jpg', title: 'Mobil dækassistance til tungvogn' },
    { src: '/_assets/annexgaardsvej-7-luftfoto-1-305.jpg', title: 'Dækcenter faciliteter i Ugerløse' },
  ],
  '/daekskift-lastbil': [
    { src: '/_assets/servicevogn-3-206.jpg', title: 'Hurtigt dækskift på stedet med servicevogn' },
    { src: '/_assets/servicevogn-4-210.jpg', title: 'Professionelt udstyr til dækskift på lastbil' },
    { src: '/_assets/bjaergningsvogn-autohjaelp-havareret-lastbil.jpg', title: 'Akut hjælp ved punktering og dækskade' },
  ],
};

export default function LandingContentPage({ forcedPath }) {
  const location = useLocation();
  const currentPath = forcedPath || location.pathname;
  const page = useMemo(() => getSeoPageByPath(currentPath), [currentPath]);
  const proseLinker = useMemo(() => createProseLinker(currentPath), [currentPath]);

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
    website_url: '' // honeypot
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const eventIdRef = useRef(null);

  if (!page) {
    return (
      <div className="py-24 text-center">
        <h1 className="text-3xl font-bold font-condensed uppercase">Siden blev ikke fundet</h1>
        <p className="mt-4 text-zinc-600">Vi kunne ikke finde det ønskede indhold.</p>
        <Link to="/" className="mt-6 inline-block px-6 py-3 bg-red-600 text-white rounded font-bold font-condensed uppercase">
          Tilbage til forsiden
        </Link>
      </div>
    );
  }

  const galleryImages = SERVICE_GALLERY_MAP[page.url_path] || [];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErrorMsg('');

    if (!eventIdRef.current) {
      eventIdRef.current = generateEventId();
    }
    const currentEventId = eventIdRef.current;

    try {
      const res = await fetch('/_api/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          source_page: page.url_path,
          service_title: page.seo?.title_tag || page.url_path
        })
      });

      if (!res.ok) {
        throw new Error('Kunne ikke indsende formularen. Prøv venligst igen eller ring direkte.');
      }

      await trackMetaLead({
        eventId: currentEventId,
        email: formData.email,
        phone: formData.phone,
        contentName: `Side: ${page.seo?.title_tag || page.url_path}`
      });

      eventIdRef.current = null;
      setSubmitted(true);
      setFormData({
        name: '',
        phone: '',
        email: '',
        message: '',
        website_url: ''
      });
    } catch (err) {
      setErrorMsg(err.message || 'Der opstod en fejl. Ring direkte på +45 70 700 800.');
    } finally {
      setSubmitting(false);
    }
  };

  // Extract H1 and lead paragraph for hero
  const h1Item = page.content.find((item) => item.type === 'heading' && item.tag === 'h1');
  const firstParagraph = page.content.find((item) => item.type === 'paragraph');
  const title = h1Item ? h1Item.text : page.seo?.title_tag;

  // Filter content to render in main column (skip first H1 since it is in the hero)
  let skipFirstH1 = true;
  const mainContent = page.content.filter((item) => {
    if (skipFirstH1 && item.type === 'heading' && item.tag === 'h1') {
      skipFirstH1 = false;
      return false;
    }
    return true;
  });

  return (
    <>
      <SEOHead
        title={page.seo?.title_tag}
        description={page.seo?.meta_description}
        canonicalUrl={`https://mcautotransport.dk${page.url_path}`}
        schemaData={page.schema_markup}
      />

      {/* 1. HERO SECTION */}
      <section className="relative w-full bg-zinc-950 text-white py-16 sm:py-24 lg:py-28 overflow-hidden">
        {page.heroImage && (
          <img
            src={imageUrl(page.heroImage, 'width=1920,quality=80,format=auto')}
            alt={`${title} - MC Transport`}
            className="absolute inset-0 w-full h-full object-cover object-center brightness-[0.3] sm:brightness-[0.35]"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/60 to-black/40" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs sm:text-sm text-zinc-400 font-medium mb-6 uppercase tracking-wider font-condensed">
            <Link to="/" className="hover:text-white transition-colors">Hjem</Link>
            <i className="fa-solid fa-chevron-right text-[10px] text-zinc-500" aria-hidden="true" />
            <Link to="/ydelser" className="hover:text-white transition-colors">Ydelser</Link>
            <i className="fa-solid fa-chevron-right text-[10px] text-zinc-500" aria-hidden="true" />
            <span className="text-red-400 font-bold truncate max-w-[200px] sm:max-w-none">{title}</span>
          </nav>

          <div className="max-w-3xl">
            {page.badge && (
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-red-600/90 text-white text-xs font-bold uppercase tracking-wider mb-4 border border-red-500/50 shadow-md">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
                </span>
                <span>{page.badge}</span>
              </div>
            )}

            <h1 className="font-condensed text-3xl sm:text-5xl lg:text-6xl font-extrabold uppercase tracking-tight text-white leading-[1.08]">
              {title}
            </h1>

            {firstParagraph && (
              <p className="mt-4 sm:mt-6 text-base sm:text-lg lg:text-xl text-zinc-200 leading-relaxed font-normal">
                {proseLinker.format(firstParagraph.text, {
                  companyLinkClass: "font-semibold text-white hover:text-red-400 underline decoration-red-500 underline-offset-2 transition-colors",
                  linkClass: "text-red-400 font-medium hover:text-red-300 underline decoration-red-400/40 underline-offset-2 transition-colors"
                })}
              </p>
            )}

            {/* Quick action buttons */}
            <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
              <a
                href="tel:+4570700800"
                className="group inline-flex items-center justify-center gap-3 px-6 py-4 bg-red-600 hover:bg-red-700 active:bg-red-800 text-white font-condensed text-xl font-bold uppercase tracking-wider rounded shadow-xl transition-all hover:scale-[1.02] border-t border-red-400/50 text-center"
              >
                <i className="fa-solid fa-phone text-lg group-hover:animate-bounce" aria-hidden="true" />
                <span>Ring vagtcentral: +45 70 700 800</span>
              </a>

              <a
                href="#kontaktformular"
                className="inline-flex items-center justify-center gap-2 px-6 py-4 bg-zinc-900/90 hover:bg-zinc-800 text-white font-condensed text-xl font-bold uppercase rounded border border-white/20 transition-colors text-center"
              >
                <span>Indhent tilbud</span>
                <i className="fa-solid fa-arrow-down text-xs" aria-hidden="true" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 2. MAIN EDITORIAL CONTENT + SIDEBAR */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            {/* MAIN CONTENT (8 COLS) */}
            <div className="lg:col-span-8 space-y-8">
              
              {/* Render structured items from JSON */}
              <div className="space-y-6">
                {mainContent.map((item, idx) => {
                  if (item.type === 'heading') {
                    if (item.tag === 'h2') {
                      return (
                        <div key={idx} className="pt-6 first:pt-0">
                          <h2 className="font-condensed text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase tracking-tight text-neutral-900 border-b border-zinc-200 pb-3 flex items-center gap-3">
                            <span className="w-2.5 h-6 bg-red-600 rounded-sm inline-block shrink-0" />
                            <span>{item.text}</span>
                          </h2>
                        </div>
                      );
                    }
                    if (item.tag === 'h3') {
                      return (
                        <h3 key={idx} className="font-condensed text-xl sm:text-2xl font-bold uppercase tracking-tight text-zinc-900 pt-3">
                          {item.text}
                        </h3>
                      );
                    }
                    return (
                      <h4 key={idx} className="font-condensed text-lg sm:text-xl font-bold uppercase tracking-tight text-zinc-900">
                        {item.text}
                      </h4>
                    );
                  }

                  if (item.type === 'paragraph') {
                    return (
                      <p key={idx} className="text-zinc-700 text-base sm:text-lg leading-relaxed font-normal">
                        {proseLinker.format(item.text)}
                      </p>
                    );
                  }

                  if (item.type === 'list' && Array.isArray(item.items)) {
                    return (
                      <div key={idx} className="my-6 p-5 sm:p-6 bg-zinc-50 rounded-xl border border-zinc-200/80 shadow-sm">
                        <ul className="space-y-3.5">
                          {item.items.map((li, liIdx) => (
                            <li key={liIdx} className="flex items-start gap-3.5 text-zinc-800 text-base sm:text-lg leading-snug">
                              <span className="w-5 h-5 rounded-full bg-red-600 text-white flex items-center justify-center shrink-0 mt-0.5 shadow-sm">
                                <i className="fa-solid fa-check text-xs" aria-hidden="true" />
                              </span>
                              <span>{li.text}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  }

                  return null;
                })}
              </div>

              {/* SERVICE IMAGE HIGHLIGHTS */}
              {galleryImages.length > 0 && (
                <div className="pt-8 border-t border-zinc-200">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-condensed text-2xl font-bold uppercase text-zinc-950 flex items-center gap-2.5">
                      <i className="fa-solid fa-truck text-red-600" aria-hidden="true" />
                      <span>Billeder fra hverdagen</span>
                    </h3>
                    <span className="text-xs text-zinc-500 font-mono">
                      {galleryImages.length} fotos · Tryk for forstørrelse
                    </span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {galleryImages.map((gImg, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => {
                          setLightboxIndex(idx);
                          setLightboxOpen(true);
                        }}
                        className="group relative aspect-4/3 rounded-lg overflow-hidden bg-zinc-950 border border-zinc-200 focus:outline-none focus:ring-2 focus:ring-red-600 shadow-sm text-left"
                      >
                        <img
                          src={imageUrl(gImg.src, 'width=600,height=450,fit=cover,quality=80,format=auto')}
                          alt={`${gImg.title} - MC Transport`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 brightness-95"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent flex flex-col justify-end p-3">
                          <span className="text-xs font-semibold text-white drop-shadow truncate">
                            {gImg.title}
                          </span>
                        </div>
                        <div className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <i className="fa-solid fa-magnifying-glass-plus text-xs" aria-hidden="true" />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* FAQ ACCORDION */}
              {page.faq?.display_on_page && Array.isArray(page.faq?.items) && page.faq.items.length > 0 && (
                <div className="pt-10 border-t border-zinc-200">
                  <div className="mb-6">
                    <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-red-600 font-condensed bg-red-50 px-2.5 py-1 rounded">
                      <i className="fa-solid fa-circle-question" aria-hidden="true" />
                      Ofte stillede spørgsmål
                    </span>
                    <h3 className="font-condensed text-2xl sm:text-3xl font-extrabold uppercase text-zinc-950 mt-2">
                      {page.faq.section_heading?.text || 'Spørgsmål & Svar'}
                    </h3>
                    <p className="text-sm text-zinc-600 mt-1">
                      Få hurtige svar på de mest almindelige spørgsmål om denne ydelse.
                    </p>
                  </div>

                  <FaqAccordion items={page.faq.items} />
                </div>
              )}
            </div>

            {/* SIDEBAR (4 COLS, STICKY) */}
            <aside className="lg:col-span-4 space-y-6 lg:sticky lg:top-24">
              
              {/* BOX 1: 24/7 DØGNVAGT CALLOUT */}
              <div className="bg-gradient-to-br from-red-600 to-red-800 text-white rounded-xl p-6 shadow-xl border-t-2 border-red-400 relative overflow-hidden">
                {/* Background truck grill lines */}
                <div className="absolute right-0 top-0 bottom-0 w-24 opacity-10 flex flex-col justify-around py-3 pointer-events-none">
                  <div className="h-1 bg-white rounded" />
                  <div className="h-1 bg-white rounded" />
                  <div className="h-1 bg-white rounded" />
                  <div className="h-1 bg-white rounded" />
                </div>

                <div className="relative z-10">
                  <span className="inline-block px-2.5 py-0.5 rounded bg-black/30 text-white text-[11px] font-bold uppercase tracking-wider mb-3">
                    Akut Assistance 24/7/365
                  </span>
                  <h3 className="font-condensed text-2xl sm:text-3xl font-bold uppercase tracking-tight text-white leading-tight">
                    Brug for hjælp nu?
                  </h3>
                  <p className="mt-2 text-sm text-red-100 leading-relaxed font-normal">
                    Vores vagtcentral er døgnåben. Ring direkte og få vurderet din opgave af erfarne fagfolk.
                  </p>

                  <a
                    href="tel:+4570700800"
                    className="mt-5 w-full flex items-center justify-center gap-3 px-4 py-3.5 bg-white hover:bg-zinc-100 active:bg-zinc-200 text-red-700 font-condensed text-xl font-bold uppercase rounded shadow-lg transition-transform hover:scale-[1.02]"
                  >
                    <i className="fa-solid fa-phone-volume text-lg" aria-hidden="true" />
                    <span>+45 70 700 800</span>
                  </a>
                </div>
              </div>

              {/* BOX 2: QUICK QUOTE / INQUIRY FORM */}
              <div id="kontaktformular" className="bg-zinc-50 rounded-xl p-6 border border-zinc-200 shadow-sm scroll-mt-28">
                <h3 className="font-condensed text-xl font-bold uppercase tracking-tight text-zinc-950 mb-1">
                  Få et uforpligtende tilbud
                </h3>
                <p className="text-xs text-zinc-600 mb-4">
                  Udfyld formularen, så vender vi hurtigt tilbage med pris og løsning.
                </p>

                {submitted ? (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-center">
                    <i className="fa-solid fa-circle-check text-green-600 text-3xl mb-2" aria-hidden="true" />
                    <h4 className="font-condensed text-lg font-bold uppercase text-green-900">Tak for din besked!</h4>
                    <p className="text-xs text-green-700 mt-1">
                      Vi behandler din forespørgsel hurtigst muligt.
                    </p>
                    <button
                      type="button"
                      onClick={() => setSubmitted(false)}
                      className="mt-3 text-xs text-green-800 underline font-semibold"
                    >
                      Send en ny besked
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-3">
                    {/* Antispam Honeypot */}
                    <div className="sr-only absolute left-[-9999px]" aria-hidden="true">
                      <label htmlFor="landing_website_url">Website</label>
                      <input
                        type="text"
                        id="landing_website_url"
                        name="website_url"
                        value={formData.website_url}
                        onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                        tabIndex={-1}
                        autoComplete="off"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold uppercase text-zinc-700 font-condensed mb-1">
                        Navn *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Dit navn eller virksomhed"
                        className="w-full px-3 py-2 text-sm bg-white border border-zinc-300 rounded focus:ring-2 focus:ring-red-600 focus:outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold uppercase text-zinc-700 font-condensed mb-1">
                        Telefon *
                      </label>
                      <input
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="Dit telefonnummer"
                        className="w-full px-3 py-2 text-sm bg-white border border-zinc-300 rounded focus:ring-2 focus:ring-red-600 focus:outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold uppercase text-zinc-700 font-condensed mb-1">
                        E-mail
                      </label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="Din e-mailadresse"
                        className="w-full px-3 py-2 text-sm bg-white border border-zinc-300 rounded focus:ring-2 focus:ring-red-600 focus:outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold uppercase text-zinc-700 font-condensed mb-1">
                        Opgavebeskrivelse *
                      </label>
                      <textarea
                        required
                        rows={3}
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        placeholder="Hvad skal flyttes, hvorfra og hvortil?"
                        className="w-full px-3 py-2 text-sm bg-white border border-zinc-300 rounded focus:ring-2 focus:ring-red-600 focus:outline-none"
                      />
                    </div>

                    {errorMsg && (
                      <div className="p-2 text-xs bg-red-50 border border-red-200 text-red-700 rounded">
                        {errorMsg}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full py-3 bg-red-600 hover:bg-red-700 active:bg-red-800 disabled:opacity-50 text-white font-condensed text-base font-bold uppercase rounded shadow transition-colors"
                    >
                      {submitting ? 'Sender...' : 'Send forespørgsel'}
                    </button>
                  </form>
                )}
              </div>

              {/* BOX 3: FORDELE / TRUST */}
              <div className="bg-white rounded-xl p-5 border border-zinc-200 shadow-sm">
                <h4 className="font-condensed text-base font-bold uppercase tracking-wider text-zinc-950 mb-3 flex items-center gap-2">
                  <i className="fa-solid fa-shield-halved text-red-600" aria-hidden="true" />
                  <span>Hvorfor vælge MC Transport</span>
                </h4>
                <ul className="space-y-2.5 text-xs sm:text-sm text-zinc-700">
                  <li className="flex items-start gap-2">
                    <i className="fa-solid fa-check text-red-600 mt-0.5" aria-hidden="true" />
                    <span>Døgnåben vagtcentral 365 dage om året</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="fa-solid fa-check text-red-600 mt-0.5" aria-hidden="true" />
                    <span>Landsdækkende kørsel i hele Danmark</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="fa-solid fa-check text-red-600 mt-0.5" aria-hidden="true" />
                    <span>Topmoderne vognpark (Scania, Volvo, MAN, Uni Man)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="fa-solid fa-check text-red-600 mt-0.5" aria-hidden="true" />
                    <span>30.000 m² videoovervåget plads til opbevaring</span>
                  </li>
                </ul>
              </div>

              {/* BOX 4: ANDRE YDELSER */}
              <div className="bg-white rounded-xl p-5 border border-zinc-200 shadow-sm">
                <h4 className="font-condensed text-base font-bold uppercase tracking-wider text-zinc-950 mb-3">
                  Se også vores andre ydelser
                </h4>
                <div className="flex flex-col space-y-1.5 text-xs font-semibold text-zinc-800">
                  {SEO_PAGES_DATA.filter((p) => p.url_path !== page.url_path && p.url_path !== '/').map((p) => {
                    const h1 = p.content.find((c) => c.type === 'heading' && c.tag === 'h1')?.text || p.seo.title_tag;
                    return (
                      <Link
                        key={p.url_path}
                        to={p.url_path}
                        className="py-1 px-2 rounded hover:bg-zinc-100 hover:text-red-600 transition-colors flex items-center justify-between group"
                      >
                        <span className="truncate">{h1.split(' ')[0]} {h1.split(' ')[1] || ''}</span>
                        <i className="fa-solid fa-arrow-right text-[10px] text-zinc-400 group-hover:text-red-600 group-hover:translate-x-0.5 transition-all" aria-hidden="true" />
                      </Link>
                    );
                  })}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* 3. BOTTOM EMERGENCY CALL BANNER */}
      <section className="bg-zinc-900 text-white py-12 border-t border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-xs font-bold uppercase tracking-widest text-red-500 font-condensed">
            MC Transport · Døgnvagt i hele Danmark
          </span>
          <h2 className="font-condensed text-3xl sm:text-4xl font-extrabold uppercase tracking-tight text-white mt-2">
            Står du overfor en transport- eller redningsopgave?
          </h2>
          <p className="mt-3 text-zinc-300 max-w-2xl mx-auto text-base">
            Vi rykker hurtigt ud med det rette materiel. Kontakt os døgnet rundt på telefonen eller send en skriftlig forespørgsel.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-4">
            <a
              href="tel:+4570700800"
              className="inline-flex items-center gap-2.5 px-6 py-3.5 bg-red-600 hover:bg-red-700 text-white font-condensed text-xl font-bold uppercase rounded shadow-xl transition-transform hover:scale-105"
            >
              <i className="fa-solid fa-phone" aria-hidden="true" />
              <span>Ring +45 70 700 800</span>
            </a>
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 px-6 py-3.5 bg-zinc-800 hover:bg-zinc-700 text-white font-condensed text-xl font-bold uppercase rounded border border-zinc-700 transition-colors"
            >
              <span>Se kontaktoplysninger</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      {/* LIGHTBOX FOR IMAGES */}
      {galleryImages.length > 0 && (
        <Lightbox
          images={galleryImages.map((g) => g.src)}
          isOpen={lightboxOpen}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
          onNavigate={(newIdx) => setLightboxIndex(newIdx)}
        />
      )}
    </>
  );
}
